import React, { useState } from 'react';
import { toast } from 'sonner';

import { useUpdateConfiguration } from '@/hooks/useConfiguration';
import {
  DEFAULT_PER_SCAN_AI_BUDGET,
  MAX_PER_SCAN_AI_BUDGET,
  MIN_PER_SCAN_AI_BUDGET,
  PER_SCAN_AI_BUDGET_SETTING_NAME,
} from '@/hooks/usePerScanAIBudget';
import { ScanProfileModalFooter } from '@/sections/settings/components';
import { cn } from '@/utils/classname';

export interface PerScanAIBudgetEditFormProps {
  email?: string;
  currentBudget?: number;
  onClose?: () => void;
}

/**
 * Praetorian-only form for setting the per-scan AI budget — the max dollar
 * spend a single AI-powered capability scan (e.g. Constantine) may incur.
 * Distinct from the monthly Bifrost cap edited by AISpendBudgetWizard.
 * Single dollar input; persisted via PUT /configuration under name
 * `ai-scan-budget`.
 */
export const PerScanAIBudgetEditForm: React.FC<
  PerScanAIBudgetEditFormProps
> = ({ email, currentBudget, onClose }) => {
  const [value, setValue] = useState<string>(
    currentBudget !== undefined ? String(currentBudget) : ''
  );
  const [error, setError] = useState<string>('');

  const { mutateAsync: updateConfiguration, status } = useUpdateConfiguration({
    email,
  });
  const isSaving = status === 'pending';

  const handleSave = async () => {
    const parsed = Number(value);
    if (
      !Number.isFinite(parsed) ||
      parsed < MIN_PER_SCAN_AI_BUDGET ||
      parsed > MAX_PER_SCAN_AI_BUDGET
    ) {
      setError(
        `Enter a dollar amount between $${MIN_PER_SCAN_AI_BUDGET} and $${MAX_PER_SCAN_AI_BUDGET.toLocaleString()}.`
      );
      return;
    }

    try {
      await updateConfiguration({
        name: PER_SCAN_AI_BUDGET_SETTING_NAME,
        value: parsed,
      });
      onClose?.();
    } catch (e) {
      toast.error('Failed to update per-scan AI budget', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="per-scan-ai-budget-form">
      <div className="flex flex-col gap-2">
        <label
          htmlFor="per-scan-ai-budget"
          className="text-chariot-text-primary text-sm font-medium"
        >
          Per-scan AI budget (USD)
        </label>
        <p className="text-chariot-text-secondary text-xs">
          Max AI spend a single scan (e.g. Constantine) may incur. Must be
          between ${MIN_PER_SCAN_AI_BUDGET} and $
          {MAX_PER_SCAN_AI_BUDGET.toLocaleString()}. Default $
          {DEFAULT_PER_SCAN_AI_BUDGET}.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-sm">$</span>
          <input
            id="per-scan-ai-budget"
            type="number"
            inputMode="decimal"
            min={MIN_PER_SCAN_AI_BUDGET}
            max={MAX_PER_SCAN_AI_BUDGET}
            step="1"
            value={value}
            onChange={e => {
              setValue(e.target.value);
              setError('');
            }}
            placeholder={String(DEFAULT_PER_SCAN_AI_BUDGET)}
            aria-label="Per-scan AI budget in dollars"
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary',
              'flex-1 rounded-md border px-3 py-1.5 text-sm',
              'placeholder:text-chariot-text-secondary',
              'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
            )}
            data-testid="per-scan-ai-budget-input"
          />
        </div>
        {error && (
          <p
            className="text-xs text-red-500"
            data-testid="per-scan-ai-budget-error"
          >
            {error}
          </p>
        )}
      </div>

      <ScanProfileModalFooter
        primary={{
          label: 'Save',
          onClick: handleSave,
          disabled: isSaving || value === '',
          'data-testid': 'per-scan-ai-budget-save-button',
        }}
        cancel={
          onClose
            ? {
                onClick: onClose,
                disabled: isSaving,
                'data-testid': 'per-scan-ai-budget-cancel-button',
              }
            : undefined
        }
      />
    </div>
  );
};
